import React, { useEffect, useState } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const RED      = '#bf1e2e';
const BG       = '#080d16';
const SURFACE  = '#0d1525';
const SURFACE2 = '#0a1220';
const BORDER   = 'rgba(255,255,255,0.08)';
const DIM      = 'rgba(255,255,255,0.60)';
const PASSWORD = 'bthdhbcuba2026';

// ─── Data ─────────────────────────────────────────────────────────────────────

const STAGES = [
  { num: '01', label: 'Identity Assessment',    color: '#bf1e2e', screenshot: '/screenshots/07-identity-profile.jpg' },
  { num: '02', label: 'Career Transition',      color: '#014c8d', screenshot: '/screenshots/08-transition-path.jpg'  },
  { num: '03', label: 'Leadership Development', color: '#1a6b3c', screenshot: '/screenshots/03-leadership.jpg'       },
  { num: '04', label: 'Legacy Building',        color: '#7a3c8a', screenshot: '/screenshots/05-legacy.jpg'           },
];

const OFFERS = [
  {
    title: 'Transition and Life After Sport',
    body: 'Structured programming that helps athletes navigate the shift from competition to what is next, built around real reflection, not generic advice.',
  },
  {
    title: 'Career Mobility',
    body: 'Practical pathways connecting athletic experience to career direction, helping athletes see options they may not have considered.',
  },
  {
    title: 'Transferable Skills',
    body: 'Helping athletes identify and translate the discipline, leadership, and resilience built through sport into skills that carry into any career.',
  },
  {
    title: 'Leadership and Identity Development',
    body: 'Programming built around who an athlete is beyond their sport, not just what they have accomplished in it.',
  },
  {
    title: 'Assessments',
    body: 'A behavioral based assessment methodology that gives athletes structured reflection and self understanding, not a one time survey.',
  },
];

const PRICING_ITEMS = [
  'Platform licensing and ongoing usage',
  'Advisory and coaching',
  'Appearances and speaking engagements',
  'Workshops and activations',
];

// ─── Shared styles ────────────────────────────────────────────────────────────

const sectionHeading: React.CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontSize: 'clamp(28px, 3.5vw, 42px)',
  fontWeight: 800,
  color: '#fff',
  lineHeight: 1.05,
  letterSpacing: '-0.5px',
  margin: '0 0 20px',
  textTransform: 'uppercase',
};

const bodyText: React.CSSProperties = {
  fontSize: 16,
  lineHeight: 1.75,
  color: DIM,
  margin: 0,
};

// ─── Phone mockup ─────────────────────────────────────────────────────────────

function PhoneMockup({ screenshot, alt }: { screenshot: string; alt: string }) {
  return (
    <div style={{
      width: 168,
      height: 360,
      background: '#080808',
      borderRadius: 36,
      border: '1.5px solid rgba(255,255,255,0.16)',
      boxShadow: '0 0 0 1px rgba(0,0,0,1), 0 24px 70px rgba(0,0,0,0.85)',
      padding: '10px 5px 8px',
      position: 'relative',
      boxSizing: 'border-box',
      flexShrink: 0,
    }}>
      <div style={{
        position: 'absolute', top: 11, left: '50%',
        transform: 'translateX(-50%)',
        width: 36, height: 9,
        background: '#000', borderRadius: 5, zIndex: 3,
      }} />
      <div style={{ width: '100%', height: '100%', borderRadius: 28, overflow: 'hidden', background: '#000' }}>
        <img src={screenshot} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
      </div>
    </div>
  );
}

// ─── Exploratory badge ────────────────────────────────────────────────────────

function ExploratoryBadge({ label }: { label: string }) {
  return (
    <span style={{
      fontFamily: "'Barlow Condensed', sans-serif",
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: 2,
      color: 'rgba(255,255,255,0.35)',
      textTransform: 'uppercase',
      border: '1px dashed rgba(255,255,255,0.18)',
      borderRadius: 4,
      padding: '3px 9px',
      verticalAlign: 'middle',
      marginLeft: 14,
      position: 'relative',
      top: -3,
    }}>
      {label}
    </span>
  );
}

// ─── Password gate ────────────────────────────────────────────────────────────

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const attempt = () => {
    if (value.trim() === PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 1400);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
      padding: 24,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 380,
        textAlign: 'center',
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderTop: `3px solid ${RED}`,
        borderRadius: 12,
        padding: '44px 40px',
      }}>
        <img
          src="/bthd-logo-full-print-transparent.png"
          alt="BTHD"
          style={{ height: 54, width: 'auto', marginBottom: 10 }}
        />
        <div style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.30)',
          letterSpacing: 1.5,
          marginBottom: 28,
          fontFamily: "'Barlow Condensed', sans-serif",
          textTransform: 'uppercase',
        }}>
          × HBCU Basketball Association
        </div>
        <div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 3,
          color: RED,
          textTransform: 'uppercase',
          marginBottom: 16,
        }}>
          Private Access
        </div>
        <input
          type="password"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && attempt()}
          placeholder="Enter access code"
          autoFocus
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${error ? RED : BORDER}`,
            borderRadius: 6,
            color: '#fff',
            fontSize: 15,
            padding: '12px 14px',
            marginBottom: 12,
            outline: 'none',
            transition: 'border-color 0.2s',
            fontFamily: "'Barlow', sans-serif",
          }}
        />
        <button
          onClick={attempt}
          style={{
            width: '100%',
            background: RED,
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '13px 0',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            fontFamily: "'Barlow Condensed', sans-serif",
          }}
        >
          Access
        </button>
        {error && (
          <div style={{ color: RED, fontSize: 12, marginTop: 10 }}>
            Incorrect access code
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function HbcubaPage() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    const id = 'bthd-barlow-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&family=Barlow:wght@400;500;600&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div style={{ background: BG, minHeight: '100vh', fontFamily: "'Barlow', sans-serif", color: '#fff', overflowX: 'hidden' }}>

      {/* ── Top nav — logo pinned far left ──────────────────────────────────── */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '18px 40px',
        background: 'linear-gradient(to bottom, rgba(8,13,22,0.85) 0%, rgba(8,13,22,0) 100%)',
        pointerEvents: 'none',
      }}>
        <img
          src="/bthd-logo-full-print-transparent.png"
          alt="BTHD"
          style={{ height: 48, width: 'auto', display: 'block', pointerEvents: 'auto' }}
        />
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section style={{
        position: 'relative',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        borderBottom: `1px solid ${BORDER}`,
      }}>
        {/* Background image */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/hero-wall.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
        }} />
        {/* Gradient overlay: opaque left, fades right */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to right, rgba(8,13,22,0.97) 0%, rgba(8,13,22,0.93) 42%, rgba(8,13,22,0.60) 65%, rgba(8,13,22,0.15) 100%)',
        }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: 920, margin: '0 auto', width: '100%', padding: 'clamp(100px, 12vw, 140px) 40px clamp(60px, 8vw, 100px)' }}>

          {/* Partnership pill */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 100,
            padding: '10px 24px',
            marginBottom: 36,
          }}>
            <span style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 3,
              color: RED,
              textTransform: 'uppercase',
            }}>
              BTHD
            </span>
            <span style={{ color: 'rgba(255,255,255,0.22)', fontSize: 18 }}>×</span>
            <span style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 3,
              color: 'rgba(255,255,255,0.78)',
              textTransform: 'uppercase',
            }}>
              HBCU Basketball Association
            </span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(68px, 10vw, 116px)',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 0.9,
            letterSpacing: '-1px',
            margin: '0 0 32px',
            textTransform: 'uppercase',
          }}>
            Beyond<br />
            <span style={{ color: RED }}>The Game</span>
          </h1>

          {/* Body */}
          <p style={{ ...bodyText, maxWidth: 520, fontSize: 17 }}>
            Basketball opens doors. What happens after depends on preparation. BTHD partners with organizations who understand that an athlete's identity, purpose, and legacy extend well beyond competition. Together with HBCU Basketball Association, we are exploring how to give every athlete a plan, a pathway, and a purpose for what comes next.
          </p>
        </div>
      </section>

      {/* ── Section 2: What BTHD Offers ─────────────────────────────────────── */}
      <section style={{ padding: 'clamp(56px, 8vw, 80px) 40px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <h2 style={sectionHeading}>Built for the Full Athlete Journey</h2>
          <p style={{ ...bodyText, maxWidth: 580, marginBottom: 48 }}>
            BTHD is an athlete transition and transformation platform, built to prepare athletes for life after sport through structured, behavioral based programming.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 14 }}>
            {OFFERS.map(item => (
              <div key={item.title} style={{
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderLeft: `3px solid ${RED}`,
                borderRadius: 10,
                padding: '22px 24px',
              }}>
                <div style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: '#fff',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                }}>
                  {item.title}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(255,255,255,0.58)' }}>
                  {item.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 3: Four Stages ───────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(56px, 8vw, 80px) 40px', borderBottom: `1px solid ${BORDER}`, background: SURFACE2 }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(32px, 4vw, 54px)',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.0,
            letterSpacing: '-0.5px',
            margin: '0 0 52px',
            textTransform: 'uppercase',
          }}>
            Four Stages. <span style={{ color: RED }}>One AI-Powered Journey.</span>
          </h2>

          <div style={{
            display: 'flex',
            gap: 'clamp(20px, 3vw, 44px)',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            {STAGES.map(({ num, label, color, screenshot }) => (
              <div key={num} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <PhoneMockup screenshot={screenshot} alt={label} />
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <div style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: 10,
                    fontWeight: 800,
                    color,
                    letterSpacing: 2.5,
                    textTransform: 'uppercase',
                    marginBottom: 5,
                  }}>
                    {num}
                  </div>
                  <div style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.80)',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}>
                    {label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', letterSpacing: 0.5 }}>
              5-Dimension Behavioral Assessment · Transferable Skills · AI-Powered Sport-to-Career Mapping
            </div>
          </div>
        </div>
      </section>

      {/* ── Section 4: Workshops ─────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(56px, 8vw, 80px) 40px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <h2 style={sectionHeading}>Meeting Athletes Where They Are</h2>
          <p style={{ ...bodyText, maxWidth: 600, marginBottom: 36 }}>
            Beyond the platform, BTHD offers workshops and in person sessions designed for camps, combines, and community events. These sessions bring the BTHD approach directly to athletes in a live, interactive setting.
          </p>
          <a
            href="https://bthd.app/workshop"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: RED,
              color: '#fff',
              textDecoration: 'none',
              borderRadius: 8,
              padding: '14px 28px',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
            }}
          >
            Book a Workshop at bthd.app/workshop
          </a>
        </div>
      </section>

      {/* ── Section 5: Vision ────────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(56px, 8vw, 80px) 40px', borderBottom: `1px solid ${BORDER}`, background: SURFACE2 }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <h2 style={{ ...sectionHeading, color: 'rgba(255,255,255,0.72)' }}>
            What This Could Become
            <ExploratoryBadge label="Exploratory" />
          </h2>
          <div style={{ borderLeft: '2px dashed rgba(255,255,255,0.12)', paddingLeft: 28 }}>
            <p style={{ ...bodyText, maxWidth: 640, color: 'rgba(255,255,255,0.52)' }}>
              Beyond individual workshops, we see an opportunity to build something more lasting with HBCU Basketball Association, embedding athlete transition and development resources directly into HBCU BA's ongoing programming. This is a direction we are exploring together, not something built or finalized today. What it looks like will take shape as our conversation continues.
            </p>
          </div>
        </div>
      </section>

      {/* ── Section 6: Pricing ───────────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(56px, 8vw, 80px) 40px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ maxWidth: 920, margin: '0 auto' }}>
          <h2 style={{ ...sectionHeading, color: 'rgba(255,255,255,0.72)' }}>
            How This Could Work
            <ExploratoryBadge label="To be defined together" />
          </h2>
          <p style={{ ...bodyText, marginBottom: 32, color: 'rgba(255,255,255,0.52)' }}>
            A potential partnership could take a few different forms depending on what HBCU BA needs:
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 44px', maxWidth: 540 }}>
            {PRICING_ITEMS.map(item => (
              <li key={item} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '16px 0',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                fontSize: 16,
                color: 'rgba(255,255,255,0.58)',
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', flexShrink: 0 }} />
                {item}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(255,255,255,0.36)', fontStyle: 'italic', maxWidth: 540 }}>
            Specifics depend on athlete count, engagement scope, and event frequency. These are details we will define together, not figures set in advance.
          </p>
        </div>
      </section>

      {/* ── Section 7: CTA / Download ────────────────────────────────────────── */}
      <section style={{ padding: 'clamp(72px, 10vw, 100px) 40px', background: SURFACE2, textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 'clamp(36px, 5vw, 62px)',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.1,
            letterSpacing: '-0.5px',
            margin: '0 0 36px',
            textTransform: 'uppercase',
          }}>
            "The workshop was the taste.<br />
            The app is <span style={{ color: RED }}>your</span> personal journey."
          </h2>

          <a
            href="https://bthd.app/programs"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: RED,
              color: '#fff',
              textDecoration: 'none',
              borderRadius: 8,
              padding: '14px 32px',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              marginBottom: 56,
            }}
          >
            Explore the Programs at bthd.app/programs
          </a>

          <div style={{ display: 'flex', gap: 36, justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: 10, display: 'inline-block', marginBottom: 12 }}>
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=148x148&data=https://apps.apple.com/ca/app/bthd/id6761592236&margin=8"
                  alt="App Store QR Code"
                  style={{ width: 140, height: 140, display: 'block' }}
                />
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 2.5,
                color: 'rgba(255,255,255,0.40)',
                textTransform: 'uppercase',
              }}>
                App Store
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: 10, display: 'inline-block', marginBottom: 12 }}>
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=148x148&data=https://play.google.com/store/apps/details?id=com.bthd.app&margin=8"
                  alt="Google Play QR Code"
                  style={{ width: 140, height: 140, display: 'block' }}
                />
              </div>
              <div style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 2.5,
                color: 'rgba(255,255,255,0.40)',
                textTransform: 'uppercase',
              }}>
                Google Play
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer style={{
        background: BG,
        borderTop: `1px solid ${BORDER}`,
        padding: '36px 40px',
        textAlign: 'center',
      }}>
        <img
          src="/bthd-logo-full-print-transparent.png"
          alt="BTHD"
          style={{ height: 26, width: 'auto', opacity: 0.45, marginBottom: 12 }}
        />
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', letterSpacing: 0.5 }}>
          Bigger Than Hoop Dreams Inc. · Kitchener, Ontario · bthd.app
        </div>
      </footer>

    </div>
  );
}
